import { InsightGetSymbolsPerTypeResponse } from '@orbis-systems/orbis-api-clients/insight';
import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { type ICache } from '../Cache';
import { BondQuantityType, OrderFilterType, PriceType } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    type Apis,
    type MiddlewareConfig,
    OrderWithExtraInfo,
    Portfolio,
    PortfolioWithExtraInfo,
    Position,
    TiprankOverviewWithExtraInfo,
} from '../types';
import { formatOrders, formatPortfolio } from '../utils';
import { QuoteApi } from './QuoteApi';
import { TipranksApi } from './TipranksApi';

@injectable()
export class BrokerApi extends CachedResponseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.TipranksApi) protected readonly tipranksApi: TipranksApi,
        @inject(ApiInjectToken.QuoteApi) protected readonly quoteApi: QuoteApi,
    ) {
        super(apis, config, cache);
    }

    async getBrokerPortfolio({
        symbol,
        tipranks,
        combined,
    }: {
        symbol?: string;
        combined?: boolean;
        tipranks?: boolean;
        currency?: string;
    }): Promise<PortfolioWithExtraInfo> {
        const { data } = await this.apis.orbis.broker.portfolio({
            loadQuotes: true,
            combined,
        });
        let tipranksData: TiprankOverviewWithExtraInfo[] = [];
        let groupedSymbolsByFundType: InsightGetSymbolsPerTypeResponse | undefined = undefined;
        const portfolio = data as Portfolio;

        if (tipranks && portfolio.entries.length > 0) {
            try {
                if (symbol) {
                    tipranksData = await this.tipranksApi.getTipranksAssetsOverview(
                        // Option symbols will need to be sent as their root symbol. rootSymbol will not exist on basic equity quotes
                        portfolio.entries
                            .map((entry: Position) => entry.quote.rootSymbol || entry.quote.symbol)
                            .filter((entry) => entry === symbol),
                    );
                } else {
                    tipranksData = await this.tipranksApi.getTipranksAssetsOverview(
                        portfolio.entries.map((entry: Position) => entry.quote.rootSymbol || entry.quote.symbol),
                    );
                }
            } catch {
                // no-op
            }
        }

        try {
            groupedSymbolsByFundType = await this.quoteApi.groupSymbolsByFundType(
                portfolio.entries.map((entry: Position) => entry.symbol),
            );
        } catch {
            // no-op
        }

        return formatPortfolio(portfolio, [], [], portfolio.total || 0, {
            tipranks: tipranksData,
            symbol,
            groupedSymbolsByFundType,
        });
    }

    async getBrokerOrders(filterType: OrderFilterType = OrderFilterType.Open): Promise<OrderWithExtraInfo[]> {
        const { data } = await this.apis.orbis.broker.orders(filterType, {
            loadQuotes: true,
            bondPriceType: PriceType.Decimal,
            bondQuantityType: BondQuantityType.Unit,
        });

        return formatOrders({ orders: data, models: [], accounts: [] });
    }

    async getBrokerTransferStatus(start?: number, perPage?: number) {
        const response = await this.apis.orbis
            .getApiClient()
            .get('transfer/status/broker', { params: { start, perPage } });
        return response.data;
    }
    //TODO: this endpoint returns empty array. Check with BE
    async getBrokerOptionsUpcoming() {
        const response = await this.apis.orbis.getApiClient().get('v1/broker/portfolio/options/upcoming');
        return response.data;
    }
    //TODO: Check with BE
    async getBrokerOptionsUpcomingDetails() {
        const response = await this.apis.orbis.getApiClient().get('v1/broker/portfolio/options/upcoming/details');
        return response.data;
    }
}
